<template>
  <button
    class="whitespace-no-wrap group inline-flex items-center justify-center gap-1 transition duration-100 ease-linear focus:ring-2"
    :class="{
      'p-3': !noPadding,
      'hover:text-primary-600 px-0 text-primary': isGhost,
      'bg-secondary hover:bg-secondary-600 border-b px-0': isUnderlined,
      'font-semibold border border-primary bg-primary text-white hover:bg-primary-400':
        isPrimary,
      'font-semibold bg-secondary hover:bg-secondary-600 border text-primary hover:text-primary-400':
        isSecondary,
      'font-semibold hover:bg-secondary-300 border border-primary bg-transparent text-primary hover:text-primary-400':
        isTertiary,
      'w-full': isFullWidth,
      'animate-pulse cursor-not-allowed': loading,
      'bg-lightGold': isGold,
      'font-semibold hover:bg-secondary-600 gap-2 border px-2 py-1 text-base capitalize text-gray-secondary hover:text-primary':
        isMuted,
      'text-primary': isActive,
      uppercase: isCapitalised,
      'cursor-not-allowed border-none !bg-gray-tertiary !text-gray-secondary hover:bg-gray-tertiary':
        disabled,
    }"
    :disabled="disabled"
    :type="nativeType"
    @click="clickHandler"
  >
    <slot name="icon">
      <Icon
        v-if="icon"
        :icon="icon"
        class="inline-block size-5"
        :class="textColorClasses"
      />
    </slot>

    <slot />
    <slot name="badge" :badge="badge">
      <Transition
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        enter-active-class="transition ease-linear duration-200"
        leave-active-class="transition ease-linear duration-200"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <span v-show="badge" :class="textColorClasses"> ({{ badge }}) </span>
      </Transition>
    </slot>
  </button>
</template>

<script setup lang="ts">
type ButtonType =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'ghost'
  | 'gold'
  | 'muted'
  | 'underlined'

const props = withDefaults(
  defineProps<{
    type?: ButtonType
    nativeType?: 'button' | 'submit' | 'reset'
    noPadding?: boolean
    icon?: string
    disabled?: boolean
    isFullWidth?: boolean
    loading?: boolean
    badge?: number
    ignoreCapitalisation?: boolean
    stopPropagation?: boolean
    preventDefault?: boolean
    isActive?: boolean
  }>(),
  {
    type: 'primary',
    isActive: false,
    stopPropagation: true,
    preventDefault: true,
    ignoreCapitalisation: false,
    badge: 0,
    loading: false,
    nativeType: 'button',
    icon: '',
    noPadding: false,
    disabled: false,
  },
)

const emit = defineEmits(['click'])

const isPrimary = computed(() => props.type === 'primary')
const isSecondary = computed(() => props.type === 'secondary')
const isTertiary = computed(() => props.type === 'tertiary')
const isGhost = computed(() => props.type === 'ghost')
const isGold = computed(() => props.type === 'gold')
const isMuted = computed(() => props.type === 'muted')
const isUnderlined = computed(() => props.type === 'underlined')
const isCapitalised = computed(
  () =>
    !props.ignoreCapitalisation &&
    (isPrimary.value || isSecondary || isTertiary),
)

const clickHandler = (e: MouseEvent) => {
  if (props.nativeType !== 'submit') {
    if (props.preventDefault) {
      e.preventDefault()
    }
    if (props.stopPropagation) {
      e.stopPropagation()
    }
  }
  emit('click')
}

const textColorClasses = computed(() => ({
  'text-white': isPrimary.value,
  'text-primary-100': isSecondary.value || isMuted.value,
  'text-primary': isTertiary.value || isGhost.value,
}))
</script>
